// messageCenter/quickReport.js — "report a problem" from any backend page (bigplan backend/message-center, phase
// quick-report). A topo2 icon button with a bug in the admin menu bar, left of the menu button (entry #quickReport,
// written by header_admin.php only for users with ticketCreate, decision D-17) opens the new-ticket form in a floating
// window over the current page (view/floatingWindow: movable, minimisable, the page stays usable — step 5a). The form
// is the one of the inbox page (ticketCreateView); the report gets the context of the page it was sent from, shown to
// the user before sending. After sending the window closes and a link to the new ticket appears for a while in a small
// box below the button (the menu bar keeps its width). Closing keeps what was typed.

const SENT_NOTICE_MS = 12000;
import { str } from '../i18n/str.js';
import { createDisposers } from '../core/disposers.js';
import { createMessageCenterApi } from './messageCenterApi.js';
import { createTicketCreateView } from './ticketCreateView.js';
import { createAreaSource } from './areaInput.js';
import { ticketTextEntities } from './ticketText.js';
import { FALLBACK_LIMITS } from './attachmentPicker.js';
import { ticketRef } from './ticketLabels.js';
import { createFloatingWindow } from '../view/floatingWindow.js';

const ticketPageHref = (id) => '/messagecenter.php?ticket=' + encodeURIComponent(id);

/** What the report says about the page it was sent from. `env` is window (injectable for the test). */
export function collectContext(env, appVersion = '') {
    const { location, navigator, screen, document } = env;
    return {
        url: location.href,
        page: location.pathname,
        title: document.title,
        doc: new URLSearchParams(location.search).get('doc'),   // an opened document, where the page shows it in the URL
        user_agent: navigator.userAgent,
        language: document.documentElement.lang || null,
        viewport: env.innerWidth + 'x' + env.innerHeight,
        screen: screen ? screen.width + 'x' + screen.height : null,
        pixel_ratio: env.devicePixelRatio ?? null,
        app_version: appVersion || null,
        reported_at: new Date().toISOString(),
    };
}

function readJson(element, key, fallback) {
    try { return JSON.parse(element.dataset[key] || 'null') ?? fallback; }
    catch (e) { console.error('quick report: unreadable data-' + key, e); return fallback; }
}

/** Mounts the quick report where the page carries #quickReport; a no-op elsewhere. */
export function mountQuickReport({ doc = document, api = createMessageCenterApi(), env = window } = {}) {
    const container = doc.getElementById('quickReport');
    if (!container) { return null; }
    return createQuickReport({
        container,
        api,
        env,
        limits: readJson(container, 'uploadLimits', FALLBACK_LIMITS),
        canManage: readJson(container, 'canManage', false),
        raw: readJson(container, 'editorRaw', false),
        appVersion: container.dataset.appVersion || '',
    });
}

export function createQuickReport({ container, api, env = window, limits = FALLBACK_LIMITS, canManage = false, raw = false,
                                     appVersion = '', shrink }) {
    const disposers = createDisposers();
    let context = null;
    let assigneesLoaded = false;
    const areas = createAreaSource(api);   // loaded when the field „Bereich“ is first used, not on every page

    let noticeTimer = null;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'topo2-btn topo2-btn--outline topo2-btn--icon topo2-btn--small quickReport__open';
    button.title = str('MC_QUICK_REPORT');
    button.setAttribute('aria-label', button.title);
    const icon = document.createElement('span');
    icon.className = 'topo2-ic topo2-ic--bug';
    button.append(icon);
    const notice = document.createElement('span');
    notice.className = 'quickReport__notice';
    notice.hidden = true;
    disposers.add(() => clearTimeout(noticeTimer));

    const win = createFloatingWindow({
        title: str('MC_QUICK_REPORT'),
        iconClass: 'topo2-ic topo2-ic--bug',
        className: 'quickReport__window',
        labels: { minimize: str('MC_MINIMIZE'), restore: str('MC_RESTORE'), close: str('CLOSE_STR') },
        env,
    });
    disposers.add(() => win.destroy());
    const form = createTicketCreateView({
        entities: ticketTextEntities({ ticketHref: ticketPageHref }),
        limits,
        shrink,
        headingKey: '',   // the window bar carries the title
        onSubmit: (values, files) => send(values, files),
        onCancel: () => win.close(),
        raw,
        areaSource: areas.list,
    });
    disposers.add(() => form.destroy());
    const details = document.createElement('details');
    details.className = 'quickReport__context';
    const summary = document.createElement('summary');
    summary.textContent = str('MC_CONTEXT');
    const contextText = document.createElement('pre');
    details.append(summary, contextText);
    win.body.append(form.element, details);

    container.classList.add('quickReport');
    container.append(button, notice);
    document.body.append(win.element);   // outside the header: no sticky/overflow of the legacy header around it

    form.update({ canManage, assignees: [] });
    button.addEventListener('click', open);
    disposers.add(() => button.removeEventListener('click', open));

    /** Opens the window; if it is open already (maybe minimised), it is only restored — nothing typed gets lost. */
    function open() {
        if (!win.isOpen()) {
            context = collectContext(env, appVersion);
            contextText.textContent = JSON.stringify(context, null, 2);
        }
        hideNotice();
        if (canManage && !assigneesLoaded) { loadAssignees(); }
        win.open();
    }

    async function loadAssignees() {
        try {
            const response = await api.listAssignees();
            if (!response?.success) { throw new Error(response?.error ?? 'no answer'); }
            assigneesLoaded = true;
            form.update({ canManage, assignees: response.data });
        } catch (e) {
            console.error('quick report: loading the assignees failed', e);
        }
    }

    async function send(values, files) {
        form.update({ canManage, saving: true });
        try {
            const response = await api.createTicket({ ...values, context }, files);
            if (!response?.success) { throw new Error(response?.message ?? response?.error ?? 'no answer'); }
            form.update({ canManage, saving: false });
            areas.remember(values.area);
            form.reset();
            win.close();
            showSent(response.data.ticket_id);
        } catch (e) {
            console.error('quick report: sending failed', e);
            form.update({ canManage, saving: false, error: 'MC_SAVE_FAILED' });
        }
    }

    function showSent(ticketId) {
        const link = document.createElement('a');
        link.href = ticketPageHref(ticketId);
        link.textContent = str('MC_REPORT_SENT').replace('{{ref}}', () => ticketRef(ticketId));
        notice.replaceChildren(link);
        notice.hidden = false;
        clearTimeout(noticeTimer);
        noticeTimer = setTimeout(hideNotice, SENT_NOTICE_MS);
    }

    function hideNotice() {
        clearTimeout(noticeTimer);
        notice.hidden = true;
        notice.replaceChildren();
    }

    function destroy() {
        disposers.dispose();
        button.remove();
        notice.remove();
    }

    return { open, destroy };
}
